import {
  asLexThemeKey,
  DEFAULT_EXPERIENCE_MODE,
  isExperienceMode,
  isUiLocale,
  type TExperienceMode,
  type TLexThemeKey,
  type TUiLocale,
} from "@lexicanon/shared"
import { useSyncExternalStore } from "react"
import { useSession } from "../lib/auth-client"
import { getExperienceModeSnapshot, subscribeExperienceMode } from "../lib/experience-mode-store"
import type { TVoxTheme } from "../lib/theme-mode"

export interface AuthUser {
  authenticated: boolean
  id: string
  email: string
  name: string
  isAdmin: boolean
  activeOrganizationId: string | null
  /** Voxcanon dual-theme override — null means "no explicit choice yet". */
  themePreference: TVoxTheme | null
  /** Lexicanon theme override (pastel, ink, …) — null means "no choice yet".
   *  Same stored column as themePreference, read against the Lexicanon set. */
  lexThemePreference: TLexThemeKey | null
  /** Personal chrome preference. Unknown / missing values coerce to "full". */
  experienceMode: TExperienceMode
  /** Personal UI language. Null means "use device / company default". */
  uiLocale: TUiLocale | null
}

function parseThemePreference(value: unknown): TVoxTheme | null {
  return value === "fantasy" || value === "scifi" ? value : null
}

/** Coerce a session / API value to a known experience mode. Unknown → "full". */
export function parseExperienceMode(value: unknown): TExperienceMode {
  return isExperienceMode(value) ? value : DEFAULT_EXPERIENCE_MODE
}

function parseUiLocale(value: unknown): TUiLocale | null {
  return isUiLocale(value) ? value : null
}

export function useAuth(): { user: AuthUser | null; loading: boolean } {
  const { data: session, isPending } = useSession()
  const localExperience = useSyncExternalStore(
    subscribeExperienceMode,
    getExperienceModeSnapshot,
    (): TExperienceMode | null => null,
  )

  // Better Auth occasionally returns a partially-populated session object
  // (e.g. during the initial hydrate, or in browser test environments where
  // no real auth happens). Guard on `session?.user?.id` so the hook returns
  // null in those cases instead of throwing on undefined-property access.
  const user: AuthUser | null =
    session?.user?.id && session.session
      ? {
          authenticated: true,
          id: session.user.id,
          email: session.user.email,
          name: session.user.name,
          isAdmin: (session.user as Record<string, unknown>).role === "admin",
          activeOrganizationId: (session.session as Record<string, unknown>).activeOrganizationId as string | null,
          themePreference: parseThemePreference((session.user as Record<string, unknown>).themePreference),
          lexThemePreference: asLexThemeKey((session.user as Record<string, unknown>).themePreference),
          experienceMode:
            localExperience ?? parseExperienceMode((session.user as Record<string, unknown>).experienceMode),
          uiLocale: parseUiLocale((session.user as Record<string, unknown>).uiLocale),
        }
      : null

  return { user, loading: isPending }
}
